import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { RoomShellStyle, RsIcon, accentVars, cleanMode } from './roomShellStyle';

// ⋯ The top bar's one menu (the mockup's ⋯ with Export chat; PortedMenu's
// mechanics in the frame's tokens): items are passed in, each opens a surface
// the PAGE owns (Export chat, Files, Room details…) and nothing is rebuilt
// here. Portaled to <body>, fixed, following the ⋯ on scroll/resize; an
// outside press or Esc closes it; ↑/↓ walk the items.
//   items: [{ key, label, icon?, onSelect, on? }]   (`on` = a live dot)

export default function RoomShellMenu({ items = [], isMobile = false, mode = 'dark', accent = null }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const sheetRef = useRef(null);
  const menuId = `rs-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b || typeof window === 'undefined') return;
    const r = b.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const width = Math.max(200, Math.min(250, vw - 32));
    setPos({ top: r.bottom + 8, left: Math.max(16, Math.min(vw - 16 - width, r.right - width)), width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    let raf = 0;
    const follow = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (sheetRef.current && sheetRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); if (btnRef.current) btnRef.current.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const list = sheetRef.current ? [...sheetRef.current.querySelectorAll('.rs-mi')] : [];
      if (!list.length) return;
      e.preventDefault();
      const i = list.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i <= 0 ? list.length - 1 : i - 1);
      list[next].focus();
    };
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);
  const placed = !!pos;
  useEffect(() => {
    if (!open || !placed || !sheetRef.current) return;
    const first = sheetRef.current.querySelector('.rs-mi');
    if (first) first.focus({ preventScroll: true });
  }, [open, placed]);

  if (!items.length) return null;
  const live = items.some((it) => it.on);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-prop-ui=""
        data-sq="1"
        className="rs-rnd"
        data-rs-menu-btn=""
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <RsIcon name="more" size={isMobile ? 20 : 18} />
        {live ? <i aria-hidden="true" style={{ position: 'absolute', top: 5, right: 5, width: 7, height: 7, borderRadius: '50%', background: 'rgb(var(--rs-live))' }} /> : null}
      </button>
      {open && pos && typeof document !== 'undefined' ? createPortal(
        <div className="rs-layer" data-menu="1" data-rs-mode={cleanMode(mode)} style={{ ...accentVars(accent), position: 'fixed', inset: 'auto', top: 0, left: 0, width: 0, height: 0, zIndex: 100020 }}>
          <RoomShellStyle />
          <div ref={sheetRef} id={menuId} className="rs-menu" role="menu" aria-label="More" style={{ top: pos.top, left: pos.left, width: pos.width }}>
            {items.map((it) => (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                data-prop-ui=""
                data-rs-item={it.key}
                className="rs-mi"
                onClick={() => {
                  setOpen(false);
                  if (btnRef.current) btnRef.current.focus({ preventScroll: true });
                  if (it.onSelect) it.onSelect();
                }}
              >
                {it.icon ? <span className="rs-miic" aria-hidden="true">{typeof it.icon === 'string' && /^[a-z]+$/.test(it.icon) ? <RsIcon name={it.icon} size={16} /> : it.icon}</span> : null}
                <span className="rs-mil">{it.label}</span>
                {it.on ? <span className="rs-mion" aria-hidden="true" /> : null}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
